import React, { useState, useEffect } from "react";
import { calculateCombinedMetrics, generateProjection, POLICY_MODULES } from "../../../../data/origami/policySimulationData";
import ProjectionChart from "./ProjectionChart";

function SimulationAnalyzer({ simulationId, modules, onAnalysisComplete, initialAnalysis }) {
  const [interactionStep, setInteractionStep] = useState(initialAnalysis ? 3 : 0);
  const [extractedValues, setExtractedValues] = useState(
    initialAnalysis || {
      finalProductivity: null,
      peakStress: null,
      avgTurnover: null,
    }
  );
  const [showCalculating, setShowCalculating] = useState(false);
  const [calculatedScore, setCalculatedScore] = useState(initialAnalysis ? initialAnalysis.score : null);

  if (!modules || !Array.isArray(modules) || modules.length === 0) {
    return (
      <div className="bg-slate-900/60 border border-red-700 rounded-xl p-4">
        <div className="text-sm text-red-300">
          ⚠️ Erreur : Simulation {simulationId} sans modules valides
        </div>
      </div>
    );
  }

  const metrics = calculateCombinedMetrics(modules);
  const projection = generateProjection(metrics);

  const finalProductivity = Math.round(projection[projection.length - 1].productivity);
  const peakStress = Math.round(Math.max(...projection.map(p => p.stress)));
  const avgTurnover = Math.round(projection.reduce((sum, p) => sum + p.turnover, 0) / projection.length);

  const handleExtractValue = (key, value) => {
    setExtractedValues(prev => ({ ...prev, [key]: value }));
    setInteractionStep(prev => prev + 1);
  };

  const canCalculate = interactionStep >= 3 && !calculatedScore;

  const handleCalculate = () => {
    setShowCalculating(true);

    setTimeout(() => {
      const score = Math.round(
        extractedValues.finalProductivity * 0.85 +       // 85%
        (100 - extractedValues.peakStress) * 0.05 +      // 5%
        (100 - extractedValues.avgTurnover) * 0.10       // 10%
      );

      setCalculatedScore(score);
      setShowCalculating(false);
      onAnalysisComplete(score, extractedValues);
    }, 1500);
  };

  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4 space-y-4">
      {/* Header avec modules */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="text-lg font-semibold text-white mb-2">
            {simulationId}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {modules.map(id => {
              const module = POLICY_MODULES.find(m => m.id === id);
              return (
                <span
                  key={id}
                  className="px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/30 text-xs text-purple-300"
                >
                  {module?.name || id}
                </span>
              );
            })}
          </div>
        </div>

        {/* Score badge si calculé */}
        {calculatedScore !== null && (
          <div className={`
            px-4 py-2 rounded-lg font-bold text-2xl font-mono
            ${calculatedScore >= 90
              ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200'
              : calculatedScore >= 70
                ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-200'
                : 'bg-slate-700/20 border border-slate-600/40 text-slate-300'
            }
          `}>
            {calculatedScore}
          </div>
        )}
      </div>

      {/* Projection Chart */}
      <ProjectionChart
        data={projection}
        title="Projection sur 6 mois"
      />

      {/* Interactive Analysis */}
      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 space-y-3">
        <div className="text-sm font-semibold text-cyan-300">
          🔍 Analyse interactive
        </div>
        <div className="text-xs text-slate-400 mb-3">
          Extrayez les valeurs clés du graphique pour calculer le score
        </div>

        {/* Step 1: Final Productivity */}
        <div className={`
          p-3 rounded-lg border transition-all duration-300
          ${interactionStep >= 1
            ? "bg-emerald-500/10 border-emerald-500/30"
            : "bg-slate-800/40 border-slate-700"
          }
        `}>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-300">
              1. Productivité finale (Juin)
            </span>
            {interactionStep === 0 && (
              <button
                onClick={() => handleExtractValue("finalProductivity", finalProductivity)}
                className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold transition"
              >
                Extraire
              </button>
            )}
            {interactionStep >= 1 && (
              <span className="text-emerald-300 text-sm font-mono animate-pulse">
                {extractedValues.finalProductivity}%
              </span>
            )}
          </div>
        </div>

        {/* Step 2: Peak Stress */}
        <div className={`
          p-3 rounded-lg border transition-all duration-300
          ${interactionStep >= 2
            ? "bg-emerald-500/10 border-emerald-500/30"
            : interactionStep === 1
              ? "bg-slate-800/40 border-slate-700"
              : "bg-slate-800/20 border-slate-700/50 opacity-50"
          }
        `}>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-300">
              2. Pic de stress (max)
            </span>
            {interactionStep === 1 && (
              <button
                onClick={() => handleExtractValue("peakStress", peakStress)}
                className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold transition"
              >
                Extraire
              </button>
            )}
            {interactionStep >= 2 && (
              <span className="text-emerald-300 text-sm font-mono animate-pulse">
                {extractedValues.peakStress}%
              </span>
            )}
          </div>
        </div>

        {/* Step 3: Avg Turnover */}
        <div className={`
          p-3 rounded-lg border transition-all duration-300
          ${interactionStep >= 3
            ? "bg-emerald-500/10 border-emerald-500/30"
            : interactionStep === 2
              ? "bg-slate-800/40 border-slate-700"
              : "bg-slate-800/20 border-slate-700/50 opacity-50"
          }
        `}>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-300">
              3. Turnover moyen
            </span>
            {interactionStep === 2 && (
              <button
                onClick={() => handleExtractValue("avgTurnover", avgTurnover)}
                className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold transition"
              >
                Extraire
              </button>
            )}
            {interactionStep >= 3 && (
              <span className="text-emerald-300 text-sm font-mono animate-pulse">
                {extractedValues.avgTurnover}%
              </span>
            )}
          </div>
        </div>

        {/* Calculate Button */}
        {canCalculate && (
          <button
            onClick={handleCalculate}
            disabled={showCalculating}
            className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition disabled:opacity-50"
          >
            {showCalculating ? "⏳ Calcul en cours..." : "📊 Calculer le score de productivité"}
          </button>
        )}

        {/* Result Animation */}
        {showCalculating && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        )}

        {/* Score Result */}
        {calculatedScore !== null && !showCalculating && (
          <div className={`
            p-4 rounded-lg border-2 text-center animate-pulse
            ${calculatedScore >= 90
              ? 'bg-emerald-500/20 border-emerald-400'
              : calculatedScore >= 70
                ? 'bg-cyan-500/20 border-cyan-400'
                : 'bg-slate-700/20 border-slate-600'
            }
          `}>
            <div className="text-xs text-slate-300 mb-1">Score final de productivité</div>
            <div className={`
              text-4xl font-bold font-mono
              ${calculatedScore >= 90 ? 'text-emerald-200' : calculatedScore >= 70 ? 'text-cyan-200' : 'text-slate-300'}
            `}>
              {calculatedScore}%
            </div>
            {calculatedScore >= 90 && (
              <div className="text-xs text-emerald-300 mt-2">
                ✓ Objectif de productivité atteint (&gt;90%)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ComparisonPanel({ analyzedSimulations, simulationData, missionResolve }) {
  const [resolveTriggered, setResolveTriggered] = useState(false);

  useEffect(() => {
    const totalSimulations = Object.keys(simulationData).length;
    const analyzedCount = analyzedSimulations.length;

    // Si toutes les simulations sont analysées ET pas encore resolved
    if (totalSimulations > 0 && analyzedCount === totalSimulations && !resolveTriggered) {
      setResolveTriggered(true);
      missionResolve?.({ key: "s2.p3.simulation.found" });
    }
  }, [analyzedSimulations.length, simulationData, resolveTriggered, missionResolve]);



  if (analyzedSimulations.length === 0) return null;

  const sorted = [...analyzedSimulations].sort((a, b) => b.score - a.score);
  const best = sorted[0];

  return (
    <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-6">
      <div className="text-lg font-semibold text-white mb-4">
        📊 Comparatif des analyses ({analyzedSimulations.length})
      </div>

      <div className="space-y-2 mb-4">
        {sorted.map((sim, i) => {
          const simData = simulationData[sim.id];
          const modules = simData?.modules || [];

          return (
            <div
              key={sim.id}
              className={`
                p-3 rounded-lg border transition
                ${i === 0
                  ? 'bg-emerald-500/20 border-emerald-500/40'
                  : 'bg-slate-800/60 border-slate-700'
                }
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className={`
                    text-lg font-bold
                    ${i === 0 ? 'text-emerald-300' : 'text-slate-400'}
                  `}>
                    #{i + 1}
                  </span>
                  <span className={`
                    text-sm font-mono
                    ${i === 0 ? 'text-emerald-200' : 'text-slate-300'}
                  `}>
                    {sim.id}
                  </span>
                </div>
                <span className={`
                  text-2xl font-bold font-mono
                  ${i === 0 ? 'text-emerald-200' : 'text-slate-400'}
                `}>
                  {sim.score}
                </span>
              </div>

              {/* Afficher les modules */}
              <div className="flex flex-wrap gap-1.5">
                {modules.map(moduleId => {
                  const module = POLICY_MODULES.find(m => m.id === moduleId);
                  return (
                    <span
                      key={moduleId}
                      className="px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/30 text-xs text-purple-300"
                    >
                      {module?.name || moduleId}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
        <div className="text-sm text-cyan-300 mb-1">
          ✓ Combinaison recommandée
        </div>
        <div className="text-lg font-bold text-cyan-200">
          {best.id} (Score: {best.score}%)
        </div>
        {best.score >= 90 && (
          <div className="text-xs text-emerald-300 mt-1">
            ✓ Atteint l'objectif de productivité (&gt;90%)
          </div>
        )}
        <div className="text-xs text-slate-400 mt-2">
          Communiquez cette recommandation au Bureau Exécutif
        </div>
      </div>
    </div>
  );
}

export default function StressMonitor({
  simulationData,
  code,
  pseudo = "Joueur 3",
  missionResolve
}) {
  // PERSISTENCE : Sauvegarder les analyses dans localStorage
  const storageKey = `stress_analyses_${code}`;

  const [analyzedSimulations, setAnalyzedSimulations] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Sauvegarder à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(analyzedSimulations));
    } catch (e) {
      console.error("Failed to save analyses:", e);
    }
  }, [analyzedSimulations, storageKey]);

  const handleAnalysisComplete = (simulationId, score, extractedValues) => {
    setAnalyzedSimulations(prev => {
      const filtered = prev.filter(s => s.id !== simulationId);
      return [...filtered, { id: simulationId, score, extractedValues }];
    });
  };

  const pendingSimulations = Object.entries(simulationData).filter(
    ([id]) => !analyzedSimulations.find(s => s.id === id)
  );

  return (
    <div className="p-6 bg-gradient-to-br from-slate-950 via-cyan-950/20 to-slate-950 min-h-[80vh]">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-slate-900/40 border border-slate-700 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-2xl font-bold text-white mb-1">
                Monitoring de Simulation RH
              </div>
              <div className="text-sm text-slate-400">
                Analysez les projections pour identifier la politique optimale
              </div>
            </div>
            <div className="text-xs text-slate-500">
              Simulations: {Object.keys(simulationData).length} • Analyses: {analyzedSimulations.length}
            </div>
          </div>
        </div>

        {/* Empty State */}
        {Object.keys(simulationData).length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <div className="text-4xl mb-3">📊</div>
            <div className="text-sm">
              En attente des simulations du Bureau Exécutif...
            </div>
          </div>
        )}

        {/* Pending Simulations */}
        {pendingSimulations.map(([id, data]) => {
          const modules = data?.modules || [];

          if (!modules || modules.length === 0) {
            return (
              <div key={id} className="bg-slate-900/60 border border-amber-700 rounded-xl p-4">
                <div className="text-sm text-amber-300">
                  ⚠️ Simulation {id} : Données invalides
                </div>
              </div>
            );
          }

          return (
            <SimulationAnalyzer
              key={id}
              simulationId={id}
              modules={modules}
              onAnalysisComplete={(score, extractedValues) =>
                handleAnalysisComplete(id, score, extractedValues)
              }
            />
          );
        })}

        {/* Already Analyzed (restored from localStorage) */}
        {analyzedSimulations.map(analysis => {
          const data = simulationData[analysis.id];
          if (!data) return null; // Simulation plus disponible

          return (
            <SimulationAnalyzer
              key={analysis.id}
              simulationId={analysis.id}
              modules={data.modules}
              onAnalysisComplete={(score, extractedValues) =>
                handleAnalysisComplete(analysis.id, score, extractedValues)
              }
              initialAnalysis={{ ...analysis.extractedValues, score: analysis.score }}
            />
          );
        })}

        {/* Comparison Panel */}
        <ComparisonPanel
          analyzedSimulations={analyzedSimulations}
          simulationData={simulationData}
          missionResolve={missionResolve}
        />
      </div>
    </div>
  );
}